/* ============================================================
   SC Will It Fit: geometry engine (v3)
   ------------------------------------------------------------
   The sums behind the fit checker and nothing else. There is no
   DOM code, no sofa data, no presets and no customer-facing
   sentence in this file: the page script collects measurements,
   calls SC.fitEngine.evaluate() and turns the structured answer
   into copy. Because nothing here touches the browser it also
   runs in node, which is how tools/test-fit-engine.js and the
   brute-force oracle (tools/fit-oracle.js) hold it to account.

   THE BOX
   A sofa is a rigid box L x p x q.
     L  the SSOT's "External Width". The long dimension of a
        sofa, but not always of an armchair, which can be
        narrower than it is deep. Nothing below assumes L is the
        longest of the three.
     A  the transit height: Access Height when the legs come off,
        otherwise the taller of frame height and Access Height
     p  min(depth, A), the slim side of the cross-section
     q  max(depth, A), the fat side
   Our ranges have arms level with the back, so the box is the
   honest shape: there is no hollow to hook round a door frame.
   (v2 used access + leg height when legs stayed on, which gave
   Isobel and Felix 88 against a real frame height of 90.)
   The one exception is legs that cannot come off: see "Passage,
   legs threaded".

   THE TIERS, best to worst
     fit      proven possible with the house shrunk by the margin
     tight    proven possible as measured with at least
              TIGHT_FLOOR to spare, but not with the house shrunk
              by the margin. Handmade frames and hand-held tape
              measures both wander, so we ask to check.
     onend    the level carry fails, but standing the sofa on its
              end, leaning it through a door, part up-ending it
              or threading fixed legs through a doorway works on
              paper. Never a promise (see the end).
     team     nothing in the model works, or the only thing that
              does is too close to call. Talk to us.
   plus
     unknown  a measurement the check depends on was left blank.
              It counts as tight in the overall verdict: a blank
              is never allowed to pass as a yes.

   THE MARGIN
   One margin M (5 cm, 2 in) with one meaning: every clear
   dimension of the house is M smaller than the customer typed.
   Each check is written as pred(delta), "does this still work
   with the house shrunk by delta?". Its spare is the largest
   delta that passes, and wherever a tier is read from a spare
   it is read the same way:
     spare >= M             fit
     spare >= TIGHT_FLOOR   tight (2 cm, 0.75 in)
     spare >= 0             team, flag 'too-close', with the
                            method and the spare left on the
                            result so the page can say how close
     spare < 0              the manoeuvre fails
   Zero spare is not "it should fit": frames vary by a little
   and hands need room. A level carry that lands in the
   too-close band still tries every other manoeuvre first.
   (v2 took 5 cm off a door's width but 5 cm off the sofa's
   LENGTH at a corner, which is worth under 2 cm of wall.)

   THE MANOEUVRES
   - Passage. Straight through a door or along a corridor, met
     square on. Four ways, as (across, up):
       (p, q) (q, p)   carried on its side, either way up
       (L, p) (L, q)   walked through facing forwards, which is
                       how an armchair narrower than it is deep
                       goes through a door
     each needing  across <= w  and  up <= h. The one with the
     most spare is reported, as data.across, data.up and
     data.carry ('side' or 'forwards').
   - Passage, legs threaded. Legs that cannot be taken off are
     posts, not a slab: the body goes through and the legs are
     fed round the frame one pair at a time. Only at a doorway
     (thin: true), where the frame is thin enough to sit between
     the two pairs, and only when the ordinary ways fail or are
     too close to call. With body = H - feet:
       body <= w   and   depth <= h,   with the full margin
     gives onend, method 'thread', flag 'legs-thread'. Never fit
     or tight: whether it threads is the team's judgement. Not
     for corridors, flights or turns, where the legs travel
     sideways the whole way.
   - Corner limit. The longest rectangle of thickness t that can
     be carried round a right angle between a corridor a wide and
     a corridor b wide:
       cornerLimit(a, b, t) = min over theta in (0, 90) of
         a/sin(theta) + b/cos(theta) - t/(sin(theta)*cos(theta))
   - Turn, flat. Carried level with one short side v up and the
     other, o, lying in plan beside L. The longer of L and o has
     to make the turn and the shorter is the thickness across it:
       max(L, o) <= cornerLimit(a, b, min(L, o))
       and   v <= lowest door or ceiling
     Both choices of v are tried. For a sofa that is the old rule
     (L round the turn, slim side across, fat side up) plus the
     same on its back for a low opening.
     On a stair flight the pitch tips the box, and a box tipped
     by phi casts a floor shadow len*cos(phi) + v*sin(phi) long.
     That is LONGER than len, peaking at sqrt(len^2 + v^2), so
     the peak is the length that has to make the turn.
   - Turn, on end. Stood up and its p x q footprint walked round.
     Needs L under the lowest door or ceiling, and the tipping
     diagonal sqrt(L^2 + e^2) under the ceiling where it is stood
     up: the top corner swings through more than L on the way.
     It tips about an axis ACROSS the space, so the short side
     lying across plays no part and the other one is e. Fat side
     across leaves the slim side to tip, but only where the
     space takes the fat side:
       e = (q <= width of the space) ? p : q
     A Coco 3 seater in a 90 hall needs sqrt(224^2 + 102^2) =
     246.1 of ceiling, not 234.1.
   - Turn, lean. Taller than the door. Stood on end in the hall,
     then leaned through base first, the top lowering as it goes.
     Seen from the side this is the corner problem again, with v
     the thickness in the plane of the lean and s the side across
     the door:
       L <= cornerLimit(a, doorH, v)   and   s <= b
       and   sqrt(L^2 + q^2) <= ceiling
     The ceiling term is the FAT diagonal whichever way round it
     leans. Leaning swings it through sqrt(L^2 + v^2), and
     getting it upright in the first place, with v across the
     hall, tips it through sqrt(L^2 + s^2): one of the two is
     always the fat one. The door width shrinks with the margin
     like everything else, so a lean through a door the slim side
     barely clears is very-tight. No lean is offered into a room
     whose ceiling is given as lower than its own door (that is a
     mistyped house, and it once let a longer sofa do better than
     a shorter one), nor into a room known to be shorter than
     the sofa.
   - Turn, tilt. Carried part up-ended at an angle phi. The floor
     shadow L*cos(phi) + v*sin(phi) has to make the turn and the
     height L*sin(phi) + v*cos(phi) has to clear.
   - A ceiling nobody measured. On end or leaning under an
     unknown ceiling is onend with flag 'needs-ceiling' and
     data.needCeiling (the diagonal plus M) only while a normal
     ceiling would do: needCeiling <= TYPICAL_CEILING (240 cm,
     96 in). Past that it is team, with the same flag and figure
     so the page can still say what to measure. "Likely, provided
     your ceiling is 284" is not likely.
   - A door nobody gave a height for. A turn marked door: true
     with doorH blank is judged under an ordinary door head
     (TYPICAL_DOOR, 198 cm or 80 in) and any carry other than the
     level one is flagged 'needs-door-height' with
     data.needDoorHeight (L plus M). Without this a blank door
     height read as no door at all, and a sofa far taller than
     any door came back likely on its end. A team result carries
     the flag only while a door no taller than an ordinary
     ceiling could change it. No lean is offered: a lean needs
     the real height of the door it goes under.
   - Stairs. The flight takes the slim side (p <= w, with the
     narrower flight deciding where there are two). Headroom
     above the step edges must be v/cos(PITCH) + G, G being room
     for the hands carrying it. The foot, the landing and the top
     are turns with one leg pitched. On a landing the sofa is
     stood up across the larger landing dimension when both are
     known, else across the flight. A half landing must finish
     one turn before starting the next. Winders have no level
     floor, so only the flat carry counts. Spirals go to the team.
     A stair turn that comes out fit is held at tight
     (STAIR_TURN_CAP): newel posts, handrail returns and nosings
     decide those, and no tape measurement captures them.
   - Lift. The door is a passage. The car takes the sofa straight
     in (L <= depth, p <= width, q <= height) or stood on its
     end, the side that went across the door staying across the
     car.
   - Split frame. Where one is offered and the verdict is onend
     or team, everything is run again at L/2 + SPLIT_ALLOW.

   CONFIRMED AGAINST THE ORACLE
   tools/fit-oracle.js knows none of these formulas. It grids
   every position and angle and flood-fills. It agrees on:
   - the corner formula above;
   - a door b wide in the side wall of a hall a wide behaves like
     that corner, although the room opens out beyond it. The true
     limit there is at most a*b/t and within a few cm of
     cornerLimit, and cornerLimit is always the cautious one of
     the two, for any wall thickness and whether or not the hall
     dead-ends at the door. So turns through doors use it too;
   - the lean-through limit, which in the plane of the lean is
       min(cornerLimit(a, doorH, v), sqrt(ceilA^2 - v^2))
     to within the oracle's resolution. That includes the tipping
     diagonal: under a 240 ceiling a box 68 thick stands up to
     about 230 long, not 240. The oracle is flat, so it sees the
     lean and not the standing up before it: the engine asks for
     the fat diagonal either way, which is never the looser.
   Please do not "improve" on these without re-running it.

   TWO THINGS THIS ENGINE NEVER DOES
   1. Promise an on-end carry. On end, lean, tilt and thread can
      only ever reach the onend tier, which the page words as
      "likely, verify with us". Doors on their hinges, radiators,
      light fittings and skirting are not in the model.
   2. Say a sofa will not fit. The worst answer is team, because
      a split frame, another route, a door off its hinges or a
      twisting carry the model does not cover may still work.
   And one it always does: when in doubt it gives the lower tier.
   fit and tight are only returned for a manoeuvre the geometry
   proves possible for a rigid box.

   USING IT
     SC.fitEngine.evaluate({
       unit: 'cm' | 'in',
       margin: optional override of M,
       sofa: { L, D, H, access, legsOff, feet, splitAvailable },
       checks: [ { id, stop, kind, ... } ]     in route order
     })
   feet is the leg height, used only to thread fixed legs.
   returns { overall, results, sofa, unknown, split }. Each result
   is { id, stop, kind, tier, method, spare, shortBy, data, flags }.
   Check kinds and their fields:
     passage { w, h, thin }           w of 0 means open plan;
                                      thin is true for a doorway,
                                      false for a corridor
     turn    { a, b, door, doorH, ceilA, ceilB, lenA, lenB,
               pitched, onEndOk, assumed }
                                      door is true when the turn is
                                      through a doorway
     stairs  { shape, w, w2, headroom, foot, landing, top, open }
     lift    { doorW, doorH, carW, carD, carH, thin }
                                      thin is for the lift door
     flag    { code, tier }           a factor we cannot compute
   Blank measurements are null. Nothing is rounded in here: the
   page rounds when it prints.
   ============================================================ */
(function () {
  'use strict';

  /* ============ 1. CONSTANTS ============ */
  /* M            the safety margin
     G            hand gap on stairs: room for the arms carrying it
     SPLIT_ALLOW  how much longer than L / 2 one half of a split
                  frame is
     PITCH        the stair pitch assumed, in degrees. Kept at the
                  steep end of what is normally built, because a
                  steeper stair needs more headroom, so a gentler
                  one is covered.
     TIGHT_FLOOR  the least spare we will call tight. Under it the
                  carry works on paper but is too close to call.
     TYPICAL_CEILING  an ordinary ceiling. A carry that needs an
                  unmeasured ceiling to be higher than this is
                  not one to call likely.
     TYPICAL_DOOR an ordinary door head. It stands in for the
                  height of a doorway nobody measured, so a sofa
                  taller on end than a normal door is never called
                  likely on the strength of a blank.
     SPARE_MAX    how far spare() looks. Past this the number is
                  no longer interesting.
     SHORT_MAX    how far shortBy() looks before giving up. */
  var UNITS = {
    cm: { unit: 'cm', M: 5, G: 15, SPLIT_ALLOW: 5, PITCH: 42, TIGHT_FLOOR: 2, TYPICAL_CEILING: 240,
      TYPICAL_DOOR: 198, SPARE_MAX: 60, SHORT_MAX: 150 },
    'in': { unit: 'in', M: 2, G: 6, SPLIT_ALLOW: 2, PITCH: 38, TIGHT_FLOOR: 0.75, TYPICAL_CEILING: 96,
      TYPICAL_DOOR: 80, SPARE_MAX: 24, SHORT_MAX: 60 }
  };

  /* unknown ranks with tight: it holds the verdict down without
     pretending to be a failure. */
  var RANK = { fit: 0, tight: 1, unknown: 1, onend: 2, team: 3 };

  var RAD = Math.PI / 180;

  /* Comparisons carry a billionth of slack. Without it, binary
     floating point turns an exact fit typed in decimals into a
     miss: 66.1 - 5 is a hair under 61.1. */
  var EPS = 1e-9;

  function unitKey(unit) { return unit === 'in' ? 'in' : 'cm'; }

  /* A copy, so a caller cannot change the engine's own numbers. */
  function constants(unit) {
    var k = UNITS[unitKey(unit)];
    return { unit: k.unit, M: k.M, G: k.G, SPLIT_ALLOW: k.SPLIT_ALLOW, PITCH: k.PITCH,
      TIGHT_FLOOR: k.TIGHT_FLOOR, TYPICAL_CEILING: k.TYPICAL_CEILING,
      TYPICAL_DOOR: k.TYPICAL_DOOR, SPARE_MAX: k.SPARE_MAX, SHORT_MAX: k.SHORT_MAX };
  }

  /* ============ 2. SMALL HELPERS ============ */
  /* A measurement, or null for "not given". An empty string must
     come back null and not 0, because a width of 0 means open
     plan and would wave the sofa through. */
  function num(v) {
    if (typeof v === 'string') {
      if (!/\S/.test(v)) return null;
      v = Number(v);
    }
    if (typeof v !== 'number' || !isFinite(v) || v < 0) return null;
    return v;
  }

  function le(x, y) { return x <= y + EPS; }

  /* The smallest of the heights that were actually measured. */
  function lowest(x, y, z) {
    var all = [x, y, z], min = null, i;
    for (i = 0; i < all.length; i++) {
      if (all[i] != null && (min === null || all[i] < min)) min = all[i];
    }
    return min;
  }

  function part(tier, method, sp, sb, data, flags) {
    return { tier: tier, method: method, spare: sp, shortBy: sb, data: data, flags: flags };
  }

  /* ============ 3. THE CORNER ============ */
  /* Sweep table: 0.1 to 89.9 degrees in 0.1 degree steps. Built
     once, because cornerLimit runs inside every bisection. */
  var STEP = 0.1 * RAD;
  var SWEEP = 899;
  var INV_SIN = [];
  var INV_COS = [];
  (function () {
    var i;
    for (i = 0; i < SWEEP; i++) {
      INV_SIN.push(1 / Math.sin((i + 1) * STEP));
      INV_COS.push(1 / Math.cos((i + 1) * STEP));
    }
  })();

  function cornerAt(a, b, t, th) {
    var s = Math.sin(th), c = Math.cos(th);
    return a / s + b / c - t / (s * c);
  }

  /* Longest rectangle of thickness t that goes round a right angle
     between corridors a and b wide. At each angle the rectangle is
     pinned between the inside corner and the two outside walls, so
     the WORST angle governs. Zero when it cannot even enter. */
  function cornerLimit(a, b, t) {
    var min = Infinity, at = 0, i, f, lo, hi, m1, m2, f1, f2;
    if (!(a > 0) || !(b > 0) || !(t >= 0) || t >= a || t >= b) return 0;
    for (i = 0; i < SWEEP; i++) {
      f = a * INV_SIN[i] + b * INV_COS[i] - t * INV_SIN[i] * INV_COS[i];
      if (f < min) { min = f; at = i; }
    }
    /* The sweep lands within a tenth of a degree of the worst
       angle. Close in on it between its two neighbours: the true
       minimum is a touch lower than any sample, and lower is the
       cautious direction. */
    lo = Math.max(at, 1) * STEP;
    hi = Math.min(at + 2, SWEEP) * STEP;
    for (i = 0; i < 30; i++) {
      m1 = lo + (hi - lo) / 3;
      m2 = hi - (hi - lo) / 3;
      f1 = cornerAt(a, b, t, m1);
      f2 = cornerAt(a, b, t, m2);
      if (f1 < f2) hi = m2; else lo = m1;
      if (f1 < min) min = f1;
      if (f2 < min) min = f2;
    }
    return min > 0 ? min : 0;
  }

  /* ============ 4. SPARE AND SHORTFALL ============ */
  /* Both take pred(delta): "does this work with every clear
     dimension of the house reduced by delta?". It must be
     monotone, which every predicate in this file is. The second
     argument is how far to look: a number, or a unit name. */
  function span(max, key) {
    if (typeof max === 'number' && max > 0) return max;
    return UNITS[unitKey(max)][key];
  }

  /* The largest delta that still passes, or a negative number
     when it does not work even as measured. */
  function spare(pred, max) {
    var lo = 0, hi = span(max, 'SPARE_MAX'), mid, i, snap;
    if (!pred(0)) return -1;
    if (pred(hi)) return hi;
    for (i = 0; i < 24; i++) {
      mid = (lo + hi) / 2;
      if (pred(mid)) lo = mid; else hi = mid;
    }
    /* Bisection stops a hair short of a round answer (9.999996
       for 10), which would call an exact 5 cm of spare tight.
       Offer the rounded figure back and keep it only if it holds. */
    snap = Math.round(lo * 1000) / 1000;
    if (snap > lo && pred(snap)) lo = snap;
    return lo;
  }

  /* How much bigger the house would need to be, every dimension
     at once, for a failing check to pass. Null when it already
     passes, or when even SHORT_MAX more would not do it. */
  function shortBy(pred, max) {
    var lo = 0, hi = span(max, 'SHORT_MAX'), mid, i, snap;
    if (pred(0) || !pred(-hi)) return null;
    for (i = 0; i < 24; i++) {
      mid = (lo + hi) / 2;
      if (pred(-mid)) hi = mid; else lo = mid;
    }
    snap = Math.round(hi * 1000) / 1000;
    if (snap > 0 && snap < hi && pred(-snap)) hi = snap;
    return hi;
  }

  /* 'fit', 'tight', or null when it is not a pass: the manoeuvre
     fails, or it works with less than the floor to spare, which
     is too close to call. A fit is looked for first, so a margin
     set below the floor still means what it says. M is a number
     or a unit name; the floor defaults to that unit's (cm for a
     bare number). */
  function tierFromSpare(sp, M, floor) {
    var k = UNITS[unitKey(M)];
    if (typeof M !== 'number') M = k.M;
    if (typeof floor !== 'number') floor = k.TIGHT_FLOOR;
    if (typeof sp !== 'number' || !(sp >= 0)) return null;
    if (sp >= M) return 'fit';
    return sp >= floor ? 'tight' : null;
  }

  /* One predicate, graded. close marks one that works as measured
     but under the floor. A failing one reports its shortfall,
     with spare as the same figure made negative. */
  function grade(pred, env) {
    var sp = spare(pred, env.SPARE_MAX), tier, sb;
    if (sp >= 0) {
      tier = tierFromSpare(sp, env.M, env.TIGHT_FLOOR);
      return { tier: tier, close: tier === null, spare: sp, shortBy: null };
    }
    sb = shortBy(pred, env.SHORT_MAX);
    return { tier: null, close: false, spare: sb == null ? -env.SHORT_MAX : -sb, shortBy: sb };
  }

  /* A graded carry as a result. Too close to call keeps its
     method and its spare, so the page can say how close; an
     outright miss has neither, only the shortfall. */
  function graded(g, method, data, flags) {
    if (g.tier) return part(g.tier, method, g.spare, null, data, flags);
    if (g.close) {
      flags.push('too-close');
      return part('team', method, g.spare, null, data, flags);
    }
    return part('team', null, g.spare, g.shortBy, data, flags);
  }

  /* Standing a box up swings its top corner through more than
     its length: sqrt(L^2 + e^2), e being its thickness in the
     plane it tips in. It tips about an axis ACROSS the space, so
     the short side lying across plays no part. Fat side across
     leaves the slim side to tip, which is the best there is, but
     only a space wide enough for the fat side allows it. */
  function tipDiag(box, room) {
    var e = le(box.q, room) ? box.p : box.q;
    return Math.sqrt(box.L * box.L + e * e);
  }

  /* ============ 5. THE SOFA ============ */
  function pos(v) {
    var n = num(v);
    return n != null && n > 0 ? n : null;
  }

  function boxOf(sofa) {
    var L = pos(sofa.L), D = pos(sofa.D), H = pos(sofa.H), access = pos(sofa.access);
    var feet = pos(sofa.feet);
    var A, ok, body = null;
    if (sofa.legsOff) {
      A = access != null ? access : H;
    } else {
      /* Legs staying on: the sofa travels at its full frame height.
         Access Height only wins if the sheet has it as the taller. */
      A = Math.max(H != null ? H : 0, access != null ? access : 0);
      if (!(A > 0)) A = null;
      /* What is left of the height once the legs are set aside.
         Only a sofa whose legs stay on has one: with the legs off
         the box is already the body. */
      if (feet != null && H != null && H - feet > 0) body = H - feet;
    }
    ok = L != null && D != null && A != null;
    return { L: L, D: D, H: H, A: A, ok: ok,
      p: ok ? Math.min(D, A) : null,
      q: ok ? Math.max(D, A) : null,
      feet: feet, body: body };
  }

  /* ============ 6. THE CHECKS ============ */

  /* ---- 6.1 passage: straight through, square on ---- */
  function checkPassage(box, c, env) {
    var L = box.L, p = box.p, q = box.q, w = num(c.w), h = num(c.h);
    var data = { slim: p, fat: q, w: w, h: h };
    /* [across, up]. On its side either way up, then walked through
       facing forwards either way up. The sideways pair comes first
       so that a tie is carried the way a sofa is carried. */
    var ways = [[p, q], [q, p], [L, p], [L, q]];
    var best = null, i, g, sp;

    function way(across, up) {
      return function (d) {
        return le(across, w - d) && (h == null || le(up, h - d));
      };
    }

    if (w == null) return null;          /* stop not measured: no result */
    if (w === 0) return part('fit', 'open', null, null, data, []);

    /* The most spare wins, or failing that the least shortfall. */
    for (i = 0; i < ways.length; i++) {
      g = grade(way(ways[i][0], ways[i][1]), env);
      if (best === null || g.spare > best.spare) {
        best = g;
        best.way = i;
      }
    }
    data.across = ways[best.way][0];
    data.up = ways[best.way][1];
    data.carry = best.way > 1 ? 'forwards' : 'side';

    /* LEGS THREADED. The body across the doorway and the depth
       under the lintel, which is the sofa on its back with its
       legs pointing sideways, fed round the frame one pair at a
       time. The body with its legs pointing up or down is the
       ordinary carry and was tried above at its full height.
       It has to clear by the whole margin: a threaded carry that
       is itself only just possible is not one to call likely. */
    if (!best.tier && c.thin === true && box.body != null) {
      sp = spare(way(box.body, box.D), env.SPARE_MAX);
      if (sp >= env.M) {
        data.across = box.body;
        data.up = box.D;
        data.carry = 'side';
        data.body = box.body;
        data.feet = box.feet;
        data.spare = sp;
        /* spare itself stays null, as on every onend result. */
        return part('onend', 'thread', null, null, data, ['legs-thread']);
      }
    }
    return graded(best, 'straight', data, []);
  }

  /* ---- 6.2 turn: a right angle from a space a wide into one b wide ---- */
  /* The end of the line for a turn. Too close to call says so and
     keeps the level carry's spare. Otherwise, where the only thing
     missing was a ceiling higher than a normal one, the figure
     goes with it so the page can say what to measure. */
  function turnMiss(g, ask, data, flags) {
    if (g.close) return graded(g, 'flat', data, flags);
    if (ask != null) {
      flags.push('needs-ceiling');
      data.needCeiling = ask;
    }
    data.shortBy = g.shortBy;
    return part('team', null, g.spare, g.shortBy, data, flags);
  }

  /* opt is for the stairs code only:
       stand     the smaller side of a landing, which has to take
                 the slim side before the sofa can be stood on it
       tipRoom   the larger side of a landing, across which the
                 sofa is stood up. Elsewhere that is a.
       flatOnly  winders: stop after the level carry */
  function checkTurn(box, t, env, opt) {
    var L = box.L, p = box.p, q = box.q, M = env.M;
    var a = num(t.a), b = num(t.b);
    var doorH = num(t.doorH), ceilA = num(t.ceilA), ceilB = num(t.ceilB);
    var lenA = num(t.lenA), lenB = num(t.lenB);
    var pitched = t.pitched === true;
    var flatOnly = !!(opt && opt.flatOnly);
    var onEndOk = t.onEndOk !== false && !flatOnly;
    var stand = opt && opt.stand != null ? opt.stand : null;
    var tipRoom = opt && opt.tipRoom != null ? opt.tipRoom : a;
    /* A doorway whose height was left blank. Stood on end or tilted
       the sofa still has to pass under its head, so an ordinary door
       stands in for the missing figure and the result says so. The
       level carry is not affected: nothing we make is that tall
       lying down. */
    var doorBlank = t.door === true && doorH == null;
    var cap = lowest(doorBlank ? env.TYPICAL_DOOR : doorH, ceilA, ceilB);
    /* The least it can be across the turn: the slim side, or the
       width of a chair narrower than it is deep. */
    var least = Math.min(p, L);
    var fatDiag = Math.sqrt(L * L + q * q);
    var flags = t.assumed === true ? ['assumed'] : [];
    var data = { a: a, b: b, slim: p, fat: q, L: L, flatLimit: null, cap: cap };
    var fatUp = null, slimUp = null, g, need, ask = null, lean, deg;

    /* Every answer past the level carry rests on that stand-in. A
       team answer only says so while a real door could change it:
       no doorway is taller than an ordinary ceiling, so asking for
       the height of one a 279 sofa could stand under helps nobody. */
    function owed(result) {
      if (doorBlank && (result.tier !== 'team' || le(L + M, env.TYPICAL_CEILING))) {
        result.flags.push('needs-door-height');
        result.data.needDoorHeight = L + M;
      }
      return result;
    }

    /* The level carry with v up and o, the other short side, in
       plan beside L. The longer of L and o has to make the turn
       and the shorter is the thickness across it. On a stair the
       pitch is forced on the sofa, so the worst floor shadow over
       the pitch is the length that has to turn. */
    function flatWith(v, o) {
      var len = Math.max(L, o), thick = Math.min(L, o);
      var planLen = pitched ? Math.sqrt(len * len + v * v) : len;
      return function (d) {
        return (cap == null || le(v, cap - d)) &&
          (lenA == null || le(planLen, lenA - d)) &&
          (lenB == null || le(planLen, lenB - d)) &&
          le(planLen, cornerLimit(a - d, b - d, thick));
      };
    }
    /* Either way up, so the spare is that of the better one. */
    function flat(d) {
      return fatUp(d) || slimUp(d);
    }

    /* On end: can the p x q footprint be walked round? Either it
       sits in the corner outright, or it turns the corner as a
       small rectangle in its own right. */
    function footprint(d) {
      var A = a - d, B = b - d;
      if (stand != null && !le(p, stand - d)) return false;
      return (le(p, B) && le(q, A)) || (le(q, B) && le(p, A)) ||
        (p < A && p < B && le(q, cornerLimit(A, B, p)));
    }
    function height(d) { return cap != null && le(L, cap - d); }
    function tipUp(d) { return ceilA == null || le(tipDiag(box, tipRoom - d), ceilA - d); }

    /* Lean, one orientation: v thick in the plane of the lean and
       s across the door. 0 fails, 1 works but the ceiling it is
       stood up under was not measured, 2 works as measured, 3
       works with the house shrunk by the margin. */
    function leanRank(v, s) {
      function pass(d) {
        /* A far side known to be shorter than the sofa leaves
           nowhere for it to come down. The ceiling has to clear
           the fat diagonal whichever way round this is: v swings
           through the lean, and s swung through standing it up. */
        return le(s, b - d) &&
          (lenB == null || le(L, lenB - d)) &&
          (ceilA == null || le(fatDiag, ceilA - d)) &&
          le(L, cornerLimit(a - d, doorH - d, v));
      }
      if (!pass(0)) return 0;
      if (ceilA == null) return 1;
      return pass(M) ? 3 : 2;
    }
    /* The better of the two ways round, ranked as above. */
    function tryLean() {
      /* Leaning starts from standing on end, so no level floor
         means no lean either. */
      if (!onEndOk || doorH == null || !(L > doorH)) return 0;
      /* A room ceiling lower than the room's own door is a
         mistyped house, not somewhere to lean a sofa into. */
      if (ceilB != null && !le(doorH, ceilB)) return 0;
      return Math.max(leanRank(q, p), leanRank(p, q));
    }

    /* Tilt: the first angle, in whole degrees, at which a part
       up-ended carry gets round with the house shrunk by d. */
    function tiltAt(d) {
      var oris = [[q, p], [p, q]], A = a - d, B = b - d, C = cap - d;
      var i, v, s, lim, dg, peak, phi, th, planLen, vert;
      for (i = 0; i < oris.length; i++) {
        v = oris[i][0];
        s = oris[i][1];
        if (!(s < A && s < B)) continue;
        lim = cornerLimit(A, B, s);
        dg = Math.sqrt(L * L + v * v);
        peak = Math.atan2(L, v);
        for (phi = 5; phi <= 85; phi++) {
          /* On a stair the flight already holds it at the pitch:
             anything flatter is not a carry anyone can choose. */
          if (pitched && phi < env.PITCH) continue;
          th = phi * RAD;
          planLen = L * Math.cos(th) + v * Math.sin(th);
          vert = L * Math.sin(th) + v * Math.cos(th);
          /* Past the peak of its swing the top corner has already
             been through the full diagonal, so that must clear. */
          if (le(vert, C) && le(planLen, lim) &&
              (lenA == null || le(planLen, lenA - d)) &&
              (lenB == null || le(planLen, lenB - d)) &&
              (th <= peak || le(dg, C))) return phi;
        }
      }
      return null;
    }

    if (a == null || b == null) {
      flags.push('missing-width');
      return part('unknown', null, null, null, data, flags);
    }
    data.flatLimit = cornerLimit(a, b, least);
    /* Fat side up is the usual carry. Slim side up, on its back,
       is the better trade under a low opening and now and then on
       a stair, where it shortens the floor shadow. */
    fatUp = flatWith(q, p);
    slimUp = flatWith(p, q);

    if (least >= a || least >= b) {
      flags.push('too-narrow');
      return turnMiss(grade(flat, env), null, data, flags);
    }

    /* FLAT. One that is too close to call is kept for the end:
       every other manoeuvre gets its turn first. */
    g = grade(flat, env);
    if (g.tier) return part(g.tier, 'flat', g.spare, null, data, flags);

    if (!flatOnly) {
      /* ON END. spare stays null on every onend result: a number
         there would read as a promise. An unmeasured ceiling is
         only worth asking for while a normal one would do; past
         that the figure is held in ask for the team result. */
      if (onEndOk) {
        if (cap == null) {
          if (footprint(0)) {
            need = tipDiag(box, tipRoom) + M;
            if (le(need, env.TYPICAL_CEILING)) {
              flags.push('needs-ceiling');
              data.needCeiling = need;
              return owed(part('onend', 'onend', null, null, data, flags));
            }
            ask = need;
          }
        } else if (footprint(0) && height(0) && tipUp(0)) {
          /* A door height alone lets it through, but it still has
             to be stood up under a ceiling nobody measured. */
          need = ceilA == null ? tipDiag(box, tipRoom) + M : null;
          if (need == null || le(need, env.TYPICAL_CEILING)) {
            if (!(footprint(M) && height(M) && tipUp(M))) flags.push('very-tight');
            if (need != null) {
              flags.push('needs-ceiling');
              data.needCeiling = need;
            }
            return owed(part('onend', 'onend', null, null, data, flags));
          }
          ask = need;
        }
      }

      /* LEAN */
      lean = tryLean();
      if (lean === 1) {
        need = fatDiag + M;
        if (le(need, env.TYPICAL_CEILING)) {
          flags.push('needs-ceiling');
          data.needCeiling = need;
          return part('onend', 'lean', null, null, data, flags);
        }
        if (ask == null) ask = need;
      } else if (lean > 1) {
        if (lean === 2) flags.push('very-tight');
        return part('onend', 'lean', null, null, data, flags);
      }

      /* TILT */
      if (cap != null) {
        deg = tiltAt(M);
        if (deg == null) {
          deg = tiltAt(0);
          if (deg != null) flags.push('very-tight');
        }
        if (deg != null) {
          data.phi = deg;
          return owed(part('onend', 'tilt', null, null, data, flags));
        }
      }
    }

    return owed(turnMiss(g, ask, data, flags));
  }

  /* ---- 6.3 flag: a real-world factor we cannot compute ---- */
  function checkFlag(c) {
    var tier = RANK.hasOwnProperty(c.tier) ? c.tier : 'tight';
    return part(tier, null, null, null, {}, c.code != null ? [c.code] : []);
  }

  /* ---- 6.4 stairs ---- */
  /* A half landing: two turns back to back, and the sofa has to
     finish the first before it can start the second, so it must
     lie along the landing in between. */
  function checkHalfLanding(box, land, w, wB, env) {
    var L = box.L, p = box.p, q = box.q, M = env.M;
    var lw = num(land.width), ld = num(land.depth), lc = num(land.ceil);
    var planLen = Math.sqrt(L * L + q * q);
    var data = { a: w, b: wB, slim: p, fat: q, L: L, flatLimit: null, cap: lc, depth: ld, width: lw };
    var flags = [], g, need, ask = null;

    function flat(d) {
      return (lc == null || le(q, lc - d)) &&
        le(planLen, lw - d) &&
        le(planLen, cornerLimit(w - d, ld - d, p)) &&
        le(planLen, cornerLimit(ld - d, wB - d, p));
    }
    /* Stood on the landing. Both flights still have to take the
       slim side, or it never reaches the landing or leaves it. */
    function stands(d) {
      return le(p, Math.min(ld, lw) - d) && le(q, Math.max(ld, lw) - d) &&
        le(p, w - d) && le(p, wB - d);
    }
    /* Stood up across the larger side of the landing. The tipping
       diagonal is longer than L, so it covers both. */
    function tip(d) { return tipDiag(box, Math.max(ld, lw) - d); }
    function clears(d) { return le(tip(d), lc - d); }

    if (lw == null || ld == null) {
      flags.push('landing-not-measured');
      return part('unknown', null, null, null, data, flags);
    }
    data.flatLimit = Math.min(lw, cornerLimit(w, ld, p), cornerLimit(ld, wB, p));

    g = grade(flat, env);
    if (g.tier) return part(g.tier, 'flat', g.spare, null, data, flags);

    if (stands(0)) {
      if (lc == null) {
        /* As on any turn: only while a normal ceiling would do. */
        need = tip(0) + M;
        if (le(need, env.TYPICAL_CEILING)) {
          flags.push('needs-ceiling');
          data.needCeiling = need;
          return part('onend', 'onend', null, null, data, flags);
        }
        ask = need;
      } else if (clears(0)) {
        if (!(stands(M) && clears(M))) flags.push('very-tight');
        return part('onend', 'onend', null, null, data, flags);
      }
    }

    return turnMiss(g, ask, data, flags);
  }

  /* Winders: steps all the way round, so nowhere to stand a sofa
     and nothing but the level carry to go on. Even that needs the
     full margin before we call it tight, and it is never fit. */
  function asWinder(r) {
    if (r.tier === 'fit') {
      r.tier = 'tight';
    } else if (r.tier === 'tight') {
      r.tier = 'team';
      r.method = null;
    }
    r.flags.push('winders');
    return r;
  }

  /* The policy line: no turn on a staircase is called better than
     STAIR_TURN_CAP without a human look. Read from the public
     object so changing it there is the whole change. */
  function capStairTurn(r, open) {
    var cap = api.STAIR_TURN_CAP;
    if (!RANK.hasOwnProperty(cap) || cap === 'unknown') cap = 'tight';
    if (r.tier !== 'unknown' && RANK[r.tier] < RANK[cap]) {
      r.tier = cap;
      r.flags.push('stair-turn-cap');
    }
    /* An open banister can sometimes be lifted over. Worth saying,
       never worth a better tier. */
    if (open) r.flags.push('open-banister');
    return r;
  }

  function checkStairs(box, c, env) {
    var p = box.p, q = box.q;
    var shape = c.shape == null ? 'straight' : c.shape;
    var two = shape === 'quarter' || shape === 'half' || shape === 'winder';
    var w = num(c.w), w2 = num(c.w2), headroom = num(c.headroom);
    var open = c.open === true;
    var out = [], wB, wMin, cosP, needFatUp, needSlimUp, need, r, land, ld, lw;

    function add(name, res, isTurn) {
      if (isTurn) capStairTurn(res, open);
      res.part = name;
      out.push(res);
      return out;
    }
    function flightPred(d) { return le(p, wMin - d); }
    /* Slim side across and fat side up always goes. Fat side
       across needs less headroom, but only where the flight is
       wide enough to take it. */
    function headPred(d) {
      return le(needFatUp, headroom - d) ||
        (le(q, wMin - d) && le(needSlimUp, headroom - d));
    }

    /* Spiral first: it is one for the team whatever was measured. */
    if (shape === 'spiral') {
      return add('flight', part('team', null, null, null, { slim: p, fat: q, w: w }, ['spiral']), false);
    }
    if (!two && shape !== 'straight') {
      return add('flight', part('team', null, null, null, { slim: p, fat: q, w: w }, ['unknown-shape']), false);
    }
    if (w == null) {
      return add('flight', part('unknown', null, null, null, { slim: p, fat: q, w: null }, ['missing-width']), false);
    }

    wB = two && w2 != null ? w2 : w;
    /* The narrower flight is the one that decides: a sofa that
       cannot get up the second has not got up the stairs. */
    wMin = Math.min(w, wB);

    /* FLIGHT */
    add('flight', graded(grade(flightPred, env), 'straight', { slim: p, fat: q, w: wMin }, []), false);

    /* HEADROOM. Carried along the pitch, a side v tall stands
       v / cos(pitch) above the line of the step edges. */
    cosP = Math.cos(env.PITCH * RAD);
    needFatUp = q / cosP + env.G;
    needSlimUp = p / cosP + env.G;
    need = le(q, wMin) ? needSlimUp : needFatUp;
    if (headroom == null) {
      add('headroom', part('unknown', null, null, null, { need: need, headroom: null },
        ['headroom-not-measured']), false);
    } else {
      add('headroom', graded(grade(headPred, env), 'straight',
        { need: need, headroom: headroom }, []), false);
    }

    /* FOOT: turning onto the first flight from the space below. */
    if (c.foot != null) {
      add('foot', checkTurn(box, { a: c.foot.a, b: w, ceilA: c.foot.ceil, ceilB: null,
        pitched: true, onEndOk: true }, env, null), true);
    }

    /* LANDING */
    if (two) {
      land = c.landing || {};
      if (shape === 'half') {
        r = checkHalfLanding(box, land, w, wB, env);
      } else {
        ld = num(land.depth);
        lw = num(land.width);
        r = checkTurn(box, { a: w, b: wB, ceilA: land.ceil, ceilB: null,
          pitched: true, onEndOk: shape !== 'winder' }, env,
          { stand: ld != null && lw != null ? Math.min(ld, lw) : null,
            tipRoom: ld != null && lw != null ? Math.max(ld, lw) : null,
            flatOnly: shape === 'winder' });
        if (shape === 'winder') asWinder(r);
      }
      add('landing', r, true);
    }

    /* TOP: turning off the last flight. The narrower flight width
       is used, which is the cautious choice whichever is last. */
    if (c.top != null) {
      add('top', checkTurn(box, { a: wMin, b: c.top.depth, ceilA: c.top.ceil, ceilB: c.top.ceil,
        pitched: true, onEndOk: true }, env, null), true);
    }

    return out;
  }

  /* ---- 6.5 lift ---- */
  function checkLift(box, c, env) {
    var L = box.L, p = box.p, q = box.q, M = env.M;
    var doorW = num(c.doorW), doorH = num(c.doorH);
    var carW = num(c.carW), carD = num(c.carD), carH = num(c.carH);
    var data = { slim: p, fat: q, L: L, carW: carW, carD: carD, carH: carH, doorW: doorW, doorH: doorH };
    var out = [], g, r;

    function add(name, res) {
      res.part = name;
      out.push(res);
    }
    /* Straight in: long axis down the depth of the car. */
    function flat(d) {
      return le(L, carD - d) && le(p, carW - d) && le(q, carH - d);
    }
    /* On end. The side that goes across the door stays across the
       car: there is no room in a lift to turn the footprint. */
    function footIn(d, dw) {
      return (le(p, dw - d) && le(p, carW - d) && le(q, carD - d)) ||
        (le(q, dw - d) && le(q, carW - d) && le(p, carD - d));
    }
    function onEnd(d) {
      return le(L, Math.min(doorH, carH) - d) && footIn(d, doorW);
    }

    /* DOOR. A lift door of 0 is a blank, not open plan. */
    if (doorW == null || doorW === 0) {
      add('door', part('unknown', null, null, null, { slim: p, fat: q, w: doorW, h: doorH },
        ['lift-not-measured']));
    } else {
      add('door', checkPassage(box, { w: doorW, h: doorH, thin: c.thin }, env));
    }

    /* CAR */
    if (carW == null || carD == null || carH == null) {
      add('car', part('unknown', null, null, null, data, ['lift-not-measured']));
      return out;
    }
    /* Straight in first. As on a turn, one that is too close to
       call still lets the sofa try standing on its end. */
    g = grade(flat, env);
    if (g.tier) {
      r = part(g.tier, 'flat', g.spare, null, data, []);
    } else if (doorW == null || doorH == null) {
      /* On end depends on the door too. If the car alone would
         take it, the blank is what stands between us and an
         answer; if not, the door cannot rescue it. */
      if (le(L, carH) && footIn(0, carW)) {
        r = part('unknown', null, null, null, data, ['lift-not-measured']);
      } else {
        r = graded(g, 'flat', data, []);
      }
    } else if (onEnd(0)) {
      r = part('onend', 'onend', null, null, data, onEnd(M) ? [] : ['very-tight']);
    } else {
      r = graded(g, 'flat', data, []);
    }
    add('car', r);
    return out;
  }

  /* ============ 7. THE WHOLE ROUTE ============ */
  /* Every result leaves in the same shape and key order. */
  function finish(id, stop, kind, r) {
    return { id: id, stop: stop, kind: kind, tier: r.tier, method: r.method,
      spare: r.spare, shortBy: r.shortBy, data: r.data, flags: r.flags };
  }

  function run(box, checks, env) {
    var out = [], i, j, c, id, stop, r, parts;
    for (i = 0; i < checks.length; i++) {
      c = checks[i];
      if (!c || typeof c !== 'object') continue;
      id = c.id != null ? c.id : 'check-' + (i + 1);
      stop = c.stop != null ? c.stop : null;
      if (c.kind === 'passage') {
        r = checkPassage(box, c, env);
        if (r) out.push(finish(id, stop, 'passage', r));
      } else if (c.kind === 'turn') {
        out.push(finish(id, stop, 'turn', checkTurn(box, c, env, null)));
      } else if (c.kind === 'flag') {
        out.push(finish(id, stop, 'flag', checkFlag(c)));
      } else if (c.kind === 'stairs' || c.kind === 'lift') {
        parts = c.kind === 'stairs' ? checkStairs(box, c, env) : checkLift(box, c, env);
        for (j = 0; j < parts.length; j++) {
          out.push(finish(id + ':' + parts[j].part, c.kind, c.kind, parts[j]));
        }
      } else {
        /* A kind we do not know is a check we did not do, and a
           check we did not do must not pass in silence. */
        out.push(finish(id, stop, c.kind != null ? c.kind : null,
          part('unknown', null, null, null, {}, ['unknown-kind'])));
      }
    }
    return out;
  }

  /* The worst tier on the route, with unknown counted as tight.
     Null when nothing was checked. */
  function rollUp(results) {
    var worst = null, i, t;
    for (i = 0; i < results.length; i++) {
      t = results[i].tier === 'unknown' ? 'tight' : results[i].tier;
      if (worst === null || RANK[t] > RANK[worst]) worst = t;
    }
    return worst;
  }

  function evaluate(input) {
    var inp = input || {};
    var sofa = inp.sofa || {};
    var k = constants(inp.unit);
    var m = num(inp.margin);
    var env = { M: m != null ? m : k.M, G: k.G, PITCH: k.PITCH, SPLIT_ALLOW: k.SPLIT_ALLOW,
      TIGHT_FLOOR: k.TIGHT_FLOOR, TYPICAL_CEILING: k.TYPICAL_CEILING,
      TYPICAL_DOOR: k.TYPICAL_DOOR, SPARE_MAX: k.SPARE_MAX, SHORT_MAX: k.SHORT_MAX };
    var box = boxOf(sofa);
    var checks = Array.isArray(inp.checks) ? inp.checks : [];
    var out = { overall: null, results: [],
      sofa: { L: box.L, D: box.D, H: box.H, A: box.A, p: box.p, q: box.q },
      unknown: [], split: null };
    var i, halfLen, halfResults;

    /* A fit must stay reachable under a margin wider than the
       usual search. */
    env.SPARE_MAX = Math.max(env.SPARE_MAX, env.M);

    /* No sofa, no verdict: the same answer as no measurements. */
    if (!box.ok) return out;

    out.results = run(box, checks, env);
    out.overall = rollUp(out.results);
    for (i = 0; i < out.results.length; i++) {
      if (out.results[i].tier === 'unknown') out.unknown.push(out.results[i].id);
    }

    /* A split frame arrives as two halves and is joined in the
       room, so only the length changes. */
    if (sofa.splitAvailable && (out.overall === 'onend' || out.overall === 'team')) {
      halfLen = box.L / 2 + env.SPLIT_ALLOW;
      halfResults = run({ L: halfLen, D: box.D, p: box.p, q: box.q, feet: box.feet, body: box.body },
        checks, env);
      out.split = { halfLen: halfLen, overall: rollUp(halfResults), results: halfResults };
    }
    return out;
  }

  /* ============ 8. PUBLIC ============ */
  var api = {
    STAIR_TURN_CAP: 'tight',
    constants: constants,
    cornerLimit: cornerLimit,
    spare: spare,
    shortBy: shortBy,
    tierFromSpare: tierFromSpare,
    evaluate: evaluate
  };

  if (typeof window !== 'undefined') {
    window.SC = window.SC || {};
    window.SC.fitEngine = api;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
